import { useState, useEffect } from 'react';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import { useBulkCreateActivities, useBulkCreateActivitiesByCompanies } from '../../../models/activities/mutations';
import { useChannelTypes } from '../../../models/channels/queries';

const MODES = [
	{ value: 'todo', label: 'To-Do', desc: 'Für später planen' },
	{ value: 'log', label: 'Eintragen', desc: 'Erledigt protokollieren' },
];

/**
 * Build the bulk-create payload from form state. `scope` is the selection's request scope
 * (`{ dealIds|companyIds }` or `{ selectAll, filters }`) and is sent as-is.
 * Returns null when the title is missing so the caller can short-circuit.
 *
 * @param {Object} params
 * @param {'todo'|'log'} params.mode
 * @param {Object} params.scope
 * @param {string} params.channelTypeId
 * @param {string} params.customLabel
 * @param {string} params.title
 * @param {string} params.body
 * @param {string} params.dueAt - datetime-local value, '' when unset
 * @param {boolean} params.isCustomChannel
 * @returns {Object|null}
 */
function buildBulkPayload({ mode, scope, channelTypeId, customLabel, title, body, dueAt, isCustomChannel }) {
	if (!title.trim()) return null;
	const base = {
		...scope,
		channelTypeId: channelTypeId || null,
		customActivityLabel: isCustomChannel ? customLabel || null : null,
		title: title.trim(),
		body: body.trim() || null,
	};
	if (mode === 'log') {
		return {
			...base,
			activityType: isCustomChannel ? 'meeting' : 'communication',
			status: 'completed',
			completedAt: new Date().toISOString(),
			channelPayload: { direction: 'outbound' },
		};
	}
	return {
		...base,
		activityType: 'task',
		status: 'planned',
		dueAt: dueAt ? new Date(dueAt).toISOString() : null,
	};
}

const BulkAddActivityModal = ({ isOpen, onClose, targetType, scope, count, targetLabel, onSuccess }) => {
	const [mode, setMode] = useState('todo');
	const [channelTypeId, setChannelTypeId] = useState('');
	const [customLabel, setCustomLabel] = useState('');
	const [title, setTitle] = useState('');
	const [body, setBody] = useState('');
	const [dueAt, setDueAt] = useState('');

	const isCompanyMode = targetType === 'companies';
	const entitySingular = isCompanyMode ? 'Unternehmen' : 'Deal';

	const bulkCreateDeals = useBulkCreateActivities();
	const bulkCreateCompanies = useBulkCreateActivitiesByCompanies();
	const bulkCreate = isCompanyMode ? bulkCreateCompanies : bulkCreateDeals;

	const { data: channelTypes = [] } = useChannelTypes(false);
	const activeChannels = channelTypes.filter((c) => c.orgEnabled !== false);
	const selectedChannel = activeChannels.find((c) => c.id === channelTypeId);
	const isCustomChannel = selectedChannel?.slug === 'manual_custom';

	// Default to first available channel once channels load
	useEffect(() => {
		if (!channelTypeId && activeChannels.length > 0) {
			setChannelTypeId(activeChannels[0].id);
		}
	}, [channelTypeId, activeChannels]);

	const handleSubmit = (e) => {
		e.preventDefault();
		const payload = buildBulkPayload({
			mode,
			scope,
			channelTypeId,
			customLabel,
			title,
			body,
			dueAt,
			isCustomChannel,
		});
		if (!payload) return;
		bulkCreate.mutate(payload, {
			onSuccess: () => {
				onSuccess?.();
				onClose();
			},
		});
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} as="form" onSubmit={handleSubmit} size="m">
			<Panel.Header onClose={onClose} subtitle={`Erstellt eine Aktivität pro ${entitySingular}.`}>
				Aktivität für {targetLabel}
			</Panel.Header>
			<Panel.Body className="space-y-4">
				<div className="flex gap-1 bg-bgBase-400 rounded-control p-1">
					{MODES.map((m) => (
						<button
							key={m.value}
							type="button"
							onClick={() => setMode(m.value)}
							className={`flex-1 text-xs px-3 py-2 rounded-control transition-colors ${
								mode === m.value
									? 'bg-white text-primary-700 font-semibold'
									: 'text-text hover:text-text'
							}`}
							title={m.desc}
						>
							{m.label}
						</button>
					))}
				</div>

				<div>
					<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
						Kanal
					</label>
					<Select value={channelTypeId} onChange={(e) => setChannelTypeId(e.target.value)}>
						{activeChannels.map((c) => (
							<option key={c.id} value={c.id}>
								{c.displayName ?? c.slug}
							</option>
						))}
					</Select>
				</div>

				{isCustomChannel && (
					<div>
						<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
							Bezeichnung
						</label>
						<TextInput
							value={customLabel}
							onChange={(e) => setCustomLabel(e.target.value)}
							placeholder="z. B. Vor-Ort-Termin"
						/>
					</div>
				)}

				<div>
					<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
						Titel *
					</label>
					<TextInput
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder={mode === 'todo' ? 'z. B. Follow-Up Call' : 'z. B. Erinnerungs-Mail gesendet'}
						required
						autoFocus
					/>
				</div>

				{mode === 'todo' && (
					<div>
						<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
							Fällig am
						</label>
						<TextInput type="datetime-local" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
					</div>
				)}

				<div>
					<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
						Notiz
					</label>
					<Textarea
						value={body}
						onChange={(e) => setBody(e.target.value)}
						placeholder="Optionale Notizen…"
						className="!resize-none"
					/>
				</div>

				<div className="rounded-card border border-primary-200 bg-primary-100 px-3 py-2 text-xs text-primary-700">
					Es wird {count === 1 ? 'eine Aktivität' : `${count.toLocaleString('de-DE')} Aktivitäten`} erstellt —
					eine pro {entitySingular}. Die Aktivität wird automatisch mit dem jeweiligen Unternehmen verknüpft.
				</div>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button type="button" variant="outline" size="sm" onClick={onClose}>
					Abbrechen
				</Button>
				<Button type="submit" variant="solid" size="sm" loading={bulkCreate.isPending} disabled={!title.trim()}>
					{mode === 'todo' ? 'To-Dos anlegen' : 'Eintragen'}
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

BulkAddActivityModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	targetType: PropTypes.oneOf(['deals', 'companies']).isRequired,
	/** Request scope from useScopedSelection's toActionScope('dealIds' | 'companyIds'). */
	scope: PropTypes.object.isRequired,
	count: PropTypes.number.isRequired,
	/** Accusative phrase from describeActionTarget, e.g. "alle 150 gefilterten Unternehmen". */
	targetLabel: PropTypes.string.isRequired,
	onSuccess: PropTypes.func,
};

export default BulkAddActivityModal;
